import styles from './WheelTyreFilter.module.scss'
import React, { useEffect, useMemo, useState } from 'react'
import { FilterCondition, FilterType, useFilter } from '@/utils/hooks/useFilter'
import { usePrevious } from '@/utils/hooks/usePrevious'
import FilterBar from '../FilterBar/FilterBar'
import FilterCheckboxes from '../FilterCheckboxes/FilterCheckboxes'
import FilterSlider from '../FilterSlider/FilterSlider'
import Checkbox from '@/components/inputs/Checkbox/Checkbox'
import { useTranslation } from 'next-i18next'
import { useAppSelector } from '@/utils/hooks/useAppSelector'
import { ArticleModel } from '@/types/api'
import FilterBox from '../FilterBox/FilterBox'
import { useAppDispatch } from '@/utils/hooks/useAppDispatch'
import Button from '@/components/buttons/Button/Button'
import FilterGroupBox from '../FilterGroupBox/FilterGroupBox'
import FilterChangeHandler from '@/types/FilterChangeHandler'

type Props = {
  items: ArticleModel[]
  onFilterChange: FilterChangeHandler<ArticleModel>
}

const WheelTyreFilter = (props: Props) => {
  const prevItems = usePrevious(props.items)
  const [selectedFilter, setSelectedFilter] = useState('tyreType')
  const { t } = useTranslation('shop')

  /**
   * Custom filter conditions
   */
  const filterConditons: FilterCondition<ArticleModel>[] = useMemo(() => [
    {
      property: 'regularPrice',
      filterType: FilterType.Boolean,
      condition: (item: ArticleModel) => item.regularPrice !== null
    },
    {
      group: 'stock',
      property: 'inStock',
      filterType: FilterType.Boolean,
      condition: (item: ArticleModel) => (
        item.quantityAvailable && !item.deliveryTime && !item.estimatedDeliveryDate)
        ||
        (item.quantityAvailable < 4 && item.quantityAvailable > 0 && item.estimatedDeliveryDate !== null)
    },
    {
      group: 'stock',
      property: 'onPurchase',
      filterType: FilterType.Boolean,
      condition: (item: ArticleModel) => item.estimatedDeliveryDate !== null
    },
    {
      group: 'stock',
      property: 'toOrder',
      filterType: FilterType.Boolean,
      condition: (item: ArticleModel) => item.deliveryTime !== null
    },
    {
      property: 'onPurchase',
      filterType: FilterType.Boolean,
      condition: (item: ArticleModel) => item.estimatedDeliveryDate !== null
    },
    {
      property: 'diameter',
      filterType: FilterType.Checkbox,
    },
    {
      property: 'runFlat',
      filterType: FilterType.Boolean,
      condition: (item: ArticleModel) => item.runFlat
    },
    {
      property: 'extraLoad',
      filterType: FilterType.Boolean,
      condition: (item: ArticleModel) => item.extraLoad
    },
    {
      property: 'typeC',
      filterType: FilterType.Boolean,
      condition: (item: ArticleModel) => [30, 40, 41, 42].includes(item.productGroupId)
    },
    {
      property: 'loadIndex',
      filterType: FilterType.Slider,
      condition: (item: ArticleModel, selected: any[]) => {
        if (!selected || selected.length === 0) return true

        const loadIndex = item.loadIndex.split('/').map((v) => Number(v))
        const min = Math.min(...loadIndex)
        const max = Math.max(...loadIndex)
        return selected[0] <= min && selected[1] >= max
      }
    },
    {
      group: 'tyreType',
      property: 'summerTyre',
      filterType: FilterType.Boolean,
      condition: (item: ArticleModel) => [10, 30, 48].includes(item.productGroupId)
    },
    {
      group: 'tyreType',
      property: 'winterTyre',
      filterType: FilterType.Boolean,
      condition: (item: ArticleModel) => [21, 41, 42, 49].includes(item.productGroupId)
    },
    {
      group: 'tyreType',
      property: 'frictionTyre',
      filterType: FilterType.Boolean,
      condition: (item: ArticleModel) => [20, 40].includes(item.productGroupId)
    },
    {
      group: 'tyreType',
      property: 'allSeason',
      filterType: FilterType.Boolean,
      condition: (item: ArticleModel) => ([10, 30, 48].includes(item.productGroupId) && item.threePmsf)
    },
  ], [])

  const {
    getSelectedValues,
    getValuesByPropery,
    saveSelectedValues,
    isPropertySelected,
    filterItems,
    clearSelectedValues,
    selectedValues,
    getFilterQuery,
    setFilterFromQuery
  } = useFilter(props.items, filterConditons)

  const prevSelectedValues = usePrevious(selectedValues)

  /**
   * Items to show in filter bar
   */
  const filterBarItems = [
    {
      property: 'tyreType',
      placeholder: t('tyreType'),
      value: () => ''
    },
    {
      property: 'brand',
      placeholder: t('brand'),
      value: () => {
        const brandNames = getSelectedValues('brandName')
        return brandNames.join(', ')
      }
    },
    {
      property: 'properties',
      placeholder: t('properties'),
      value: () => ''
    },
    {
      property: 'stockStatus',
      placeholder: t('stockStatus'),
      value: () => ''
    }
  ]

  const onFilterValueChanged = (property: string, value: any, filterType: FilterType) => {
    saveSelectedValues({ property, value, filterType })
  }

  const onCheckboxChangedHandler = (checked: boolean, name: string) => {
    onFilterValueChanged(name, checked, FilterType.Boolean)
  }

  const onClickFilterType = (property: string) => {
    setSelectedFilter(property)
  }

  const onClearFilter = () => {
    clearSelectedValues()
    setSelectedFilter('tyreType')
  }

  const loadIndex = useMemo(() => {
    const loadIndex = getValuesByPropery('loadIndex') as string[]
    // split all values by / if it exists and map to an array
    const loadIndexArray = loadIndex.map((value) => value.split('/').map((v) => Number(v)))
    // flatten array
    const loadIndexFlat = loadIndexArray.flat()
    // remove duplicates, not using set 
    const loadIndexUnique = loadIndexFlat.filter((value, index, self) => self.indexOf(value) === index)
    // sort
    const loadIndexSorted = loadIndexUnique.sort((a, b) => a - b)
    return loadIndexSorted
  }, [getValuesByPropery])

  /**
   * Filter items when selected values change
   */
  useEffect(() => {
    if (prevSelectedValues !== selectedValues)
      props.onFilterChange(filterItems(), getFilterQuery())
  }, [filterConditons, filterItems, getFilterQuery, prevSelectedValues, props, selectedValues])

  /**
   * Clear selected values when items change
   */
  useEffect(() => {
    if (prevItems?.length !== props.items.length)
      clearSelectedValues()
  }, [props.items, clearSelectedValues, prevItems])

  useEffect(() => {
    setFilterFromQuery()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div>
      {props.items.length > 0 && (
        <FilterBar
          onClick={onClickFilterType}
          selected={selectedFilter}
          items={filterBarItems}
        />
      )}
      {selectedFilter === 'tyreType' && (
        <>
          <FilterBox>
            <FilterGroupBox
              label={t('tyreType')}
            >
              <Checkbox
                label={t('summerTyres')}
                name={'summerTyre'}
                checked={isPropertySelected('summerTyre')}
                onChange={onCheckboxChangedHandler}
              />
              <Checkbox
                label={t('studdedTyres')}
                name={'winterTyre'}
                checked={isPropertySelected('winterTyre')}
                onChange={onCheckboxChangedHandler}
              />
              <Checkbox
                label={t('frictionTyres')}
                name={'frictionTyre'}
                checked={isPropertySelected('frictionTyre')}
                onChange={onCheckboxChangedHandler}
              />
              <Checkbox
                label={t('allSeason')}
                name={'allSeason'}
                checked={isPropertySelected('allSeason')}
                onChange={onCheckboxChangedHandler}
              />
            </FilterGroupBox>
            <FilterBox>
              <FilterCheckboxes
                items={props.items || []}
                property={'size'}
                onClick={onFilterValueChanged}
                selected={getSelectedValues('size')}
                label={t('tyreSize') || ''}
              />
            </FilterBox>
          </FilterBox>
        </>
      )}
      {selectedFilter === 'brand' && (
        <FilterCheckboxes
          items={props.items || []}
          property={'brandName'}
          onClick={onFilterValueChanged}
          selected={getSelectedValues('brandName')}
        />
      )}
      {selectedFilter === 'stockStatus' && (
        <FilterBox>
          <Checkbox
            label={t('inStock')}
            name={'inStock'}
            checked={isPropertySelected('inStock')}
            onChange={onCheckboxChangedHandler}
          />
          <Checkbox
            label={t('toOrder')}
            name={'toOrder'}
            checked={isPropertySelected('toOrder')}
            onChange={onCheckboxChangedHandler}
          />
          <Checkbox
            label={t('onPurchase')}
            name={'onPurchase'}
            checked={isPropertySelected('onPurchase')}
            onChange={onCheckboxChangedHandler}
          />
        </FilterBox>
      )}
      {selectedFilter === 'properties' && (
        <FilterBox>
          <div className="row">
            <div className="col-xs-12 col-md-4">
              <FilterCheckboxes
                items={props.items || []}
                property={'speedIndexCode'}
                onClick={onFilterValueChanged}
                selected={getSelectedValues('speedIndexCode')}
                label={t('speedIndex') || ''}
              />
            </div>
            <div className="col-xs-12 col-md-4">
              <FilterCheckboxes
                items={props.items || []}
                property={'fuelEfficiency'}
                onClick={onFilterValueChanged}
                selected={getSelectedValues('fuelEfficiency')}
                label={t('fuelEfficiency') || ''}
              />
            </div>
            <div className="col-xs-12 col-md-4">
              <FilterSlider
                label={t('loadIndex')}
                items={props.items || []}
                property={'loadIndex'}
                showStartAndEnd={true}
                values={loadIndex}
                value={getSelectedValues('loadIndex')}
                onChange={onFilterValueChanged}
              />
            </div>
            <div className="col-xs-12 col-md-4">
              <FilterCheckboxes
                items={props.items || []}
                property={'wetGrip'}
                onClick={onFilterValueChanged}
                selected={getSelectedValues('wetGrip')}
                label={t('wetGrip') || ''}
              />
            </div>
            <div className="col-xs-12 col-md-4">
              <FilterCheckboxes
                items={props.items || []}
                property={'noiceClass'}
                onClick={onFilterValueChanged}
                selected={getSelectedValues('noiceClass')}
                label={t('noiseClass') || ''}
              />
            </div>
            <div className="col-xs-12 col-md-4">
              <FilterSlider
                label={t('noiseLevel')}
                items={props.items || []}
                property={'noiceValue'}
                showStartAndEnd={true}
                value={getSelectedValues('noiceValue')}
                onChange={onFilterValueChanged}
              />
            </div>
            <div className="col-xs-12">
              <FilterBox>
                <Checkbox
                  label={t('runFlat')}
                  name={'runFlat'}
                  checked={isPropertySelected('runFlat')}
                  onChange={onCheckboxChangedHandler}
                />
                <Checkbox
                  label={t('extraLoad')}
                  name={'extraLoad'}
                  checked={isPropertySelected('extraLoad')}
                  onChange={onCheckboxChangedHandler}
                />
                <Checkbox
                  label={t('typeC')}
                  name={'typeC'}
                  checked={isPropertySelected('typeC')}
                  onChange={onCheckboxChangedHandler}
                />
              </FilterBox>
            </div>
          </div>
        </FilterBox>
      )}
      <Button
        onClick={onClearFilter}
        label={t('resetFilters')}
        className={styles.resetButton}
        disabled={selectedValues.length === 0}
      />
    </div >
  )
}

export default WheelTyreFilter